import * as jd from 'jsdataframe';
import {Injectable} from '@angular/core';
import * as moment from 'moment';
import {DfService} from '../../shared/_services/df.service';
import {PlantRiskService} from './plant-risk.service';
import {CssService} from './css.service';
import {SbService} from '../../shared/_services/sb.service';
import {PlantRiskCountryService} from './plant-risk-country.service';
import {MercureService} from './mercure.service';
import {PosService} from './pos.service';
import {PosModel} from '../_models/pos.model';
import {PlantRiskCountryModel} from '../_models/plant-risk-country.model';
import {MercureModel} from '../_models/mercure.model';
import {PlantRiskModel, PlantRiskOutageModel} from '../_models/plant-risk.model';
import {CssModel} from '../_models/css.model';
import {XlExportService} from '../../shared/_services/xl-export.service';
import {AssetsService} from './assets.service';
import {XlSheetModel} from '../../shared/_models/xl.model';
import {FuncsService} from '../../shared/_services/funcs.service';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  private readonly NO_DATA_MSG = 'No data to download';

  constructor(
    private assetsService: AssetsService,
    private cssService: CssService,
    private mercureService: MercureService,
    private posService: PosService,
    private plantRiskCountryService: PlantRiskCountryService,
    private plantRiskService: PlantRiskService,
    private sbService: SbService,
  ) {
  }

  // FULL DOWNLOAD
  downloadAll(): void {
    let xlSheets: XlSheetModel[] = [];
    // for market fundamentals, always all countries
    const allAssets = this.assetsService.getAssets();
    const allCountries = FuncsService.distinct(allAssets.map((a) => a.label));
    // market fundamentals
    xlSheets = xlSheets.concat(this.buildMfSheets(allCountries));
    // for the other data: only selected assets/countries
    const selAssets = this.assetsService.getSelAssets();
    const selCountries = FuncsService.distinct(selAssets.map((a) => a.country));
    // add assets
    xlSheets = xlSheets.concat(this.buildAssetsSheets(selCountries));
    // add positions
    xlSheets = xlSheets.concat(this.buildPosSheets(selCountries));
    // add outage costs
    xlSheets = xlSheets.concat(this.buildOcSheets(selCountries));
    // then download
    if (!xlSheets.every((x) => !x.sheetData.length)) {
      XlExportService.exportGeneric(
        xlSheets,
        moment().format('YYYY-MM-DD') + '-warp',
      );
    } else {
      this.sbService.message(this.NO_DATA_MSG);
    }
  }

  // ASSETS
  downloadAssets(country: string): void {
    const xlSheets = this.buildAssetsSheets([country]);
    if (!xlSheets.every((x) => !x.sheetData.length)) {
      XlExportService.exportGeneric(
        xlSheets,
        moment().format('YYYY-MM-DD') + '-' + country  + '-assets',
      );
    } else {
      this.sbService.message(this.NO_DATA_MSG);
    }
  }

  private createAssetDf(asset: string, d: any[], names: string[]): any {
    let df = jd.dfFromObjArray(d || [], names);
    const newNames = names.map((v, i) => i > 0 ? asset + '_' + v : v);
    df = df.setNames(newNames);
    return df;
  }

  private buildAssetsSheets(countries: string[]): XlSheetModel[] {
    const xlSheets: XlSheetModel[] = [];
    const prAll = this.plantRiskService.getPlantRisk();
    const cssAll = this.cssService.getCss();
    for (const country of countries) {
      const prCountry: PlantRiskModel[] = prAll.filter((i) => i.country === country) || [];
      const cssCountry: CssModel[] = cssAll.filter((i) => i.country === country) || [];
      let df = jd.dfFromObjArray([], ['ts']);
      for (let i = 0; i < prCountry.length; i++) {
        const dfPr = this.createAssetDf(prCountry[i].assetLabel, prCountry[i].prData, ['ts', 'pmax', 'psched']);
        const dfCss = this.createAssetDf(cssCountry[i]?.assetLabel, cssCountry[i]?.cssData, ['ts', 'css']);
        df = jd.dfFromObjArray(DfService.dfMerge([df.toObjArray(), dfPr.toObjArray(), dfCss.toObjArray()], 'outer', 'ts'));
      }
      xlSheets.push({sheetName: country + '-assets', sheetData: df.toObjArray()});
    }
    return xlSheets;
  }

  // MARKET FUNDAMENTALS
  downloadMf(country: string): void {
    const xlSheets = this.buildMfSheets([country]);
    if (!xlSheets.every((x) => !x.sheetData.length)) {
      XlExportService.exportGeneric(
        xlSheets,
        moment().format('YYYY-MM-DD') + '-' + country + '-market-fundamentals',
      );
    } else {
      this.sbService.message(this.NO_DATA_MSG);
    }
  }

  private buildMfSheets(countries: string[]): XlSheetModel[] {
    const xlSheets: XlSheetModel[] = [];
    const prcAll = this.plantRiskCountryService.getPlantRiskCountry();
    const mercAll = this.mercureService.getMercure();
    for (const country of countries) {
      const prcCountry: PlantRiskCountryModel = prcAll.find((i) => i.country === country) || {country: country, prcData: []};
      const mercCountry: MercureModel = mercAll.find((i) => i.country === country) || {country: country, mercureData: []};
      const merged = DfService.dfMerge([prcCountry.prcData, mercCountry.mercureData], 'outer', 'ts');
      xlSheets.push({sheetName: country + '-market-fundamentals', sheetData: merged});
    }
    return xlSheets;
  }

  // POSITIONS
  downloadPos(country: string): void {
    const xlSheets = this.buildPosSheets([country]);
    if (!xlSheets.every((x) => !x.sheetData.length)) {
      XlExportService.exportGeneric(
        xlSheets,
        moment().format('YYYY-MM-DD') + '-' + country + '-positions',
      );
    } else {
      this.sbService.message(this.NO_DATA_MSG);
    }
  }

  private buildPosSheets(countries: string[]): XlSheetModel[] {
    const xlSheets: XlSheetModel[] = [];
    const posAll = this.posService.getPos();
    for (const country of countries) {
      const posCountry: PosModel = posAll.find((i) => i.country === country) || {country: country, posData: []};
      xlSheets.push({sheetName: country + '-positions', sheetData: posCountry.posData});
    }
    return xlSheets;
  }

  // OUTAGE COST
  downloadOc(country: string): void {
    const xlSheets = this.buildOcSheets([country]);
    if (!xlSheets.every((x) => !x.sheetData.length)) {
      XlExportService.exportGeneric(
        xlSheets,
        moment().format('YYYY-MM-DD') + '-' + country + '-outage-costs',
      );
    } else {
      this.sbService.message(this.NO_DATA_MSG);
    }
  }

  private buildOcSheets(countries: string[]): XlSheetModel[] {
    const xlSheets: XlSheetModel[] = [];
    const ocAll = this.plantRiskService.getPlantRiskOutage();
    for (const country of countries) {
      const ocCountry: PlantRiskOutageModel = ocAll.find((i) => i.country === country) || {country: country, outageData: []};
      xlSheets.push({sheetName: country + '-outage-costs', sheetData: ocCountry.outageData});
    }
    return xlSheets;
  }


}
